// routes/config/staffRoutes.ts
/**
 * Staff Route Configuration
 * Routes for staff management and dashboard
 */

import { lazy } from 'react';
import { ROUTES } from '../../config/routes';
import type { RouteConfig } from '../types';

const LandingPage = lazy(() => import('../../features/landing/pages/LandingPage'));
const WelcomePage = lazy(() => import('../../features/auth/pages/WelcomePage'));
const FeedbackPage = lazy(() => import('../../features/landing/pages/FeedbackPage'));
const StaffManagementPage = lazy(() => import('../../features/staff/pages/StaffManagementPage'));
const StaffDashboardPage = lazy(() => import('../../features/staff/pages/StaffDashboardPage'));
const StaffSettingsPage = lazy(() => import('../../features/staff/pages/StaffSettingsPage'));
const FloorTeamsPage = lazy(() => import('../../features/staff/pages/FloorTeamsPage'));
const ChatPage = lazy(() => import('../../features/chat/pages/ChatPage'));
const TournamentStaffPage = lazy(() => import('../../features/staff/pages/TournamentStaffPage'));
const StaffFeedbackPage = lazy(() => import('../../features/staff-feedback/pages/StaffFeedbackPage'));
const UserSettingsPage = lazy(() => import('../../features/settings/pages/UserSettingsPage'));
const TournamentDashboardPage = lazy(() => import('../../features/dashboard/pages/TournamentDashboardPage'));
const TableManagementDashboardPage = lazy(() => import('../../features/dashboard/pages/TableManagementDashboardPage'));
const DeckCheckDashboardPage = lazy(() => import('../../features/dashboard/pages/DeckCheckDashboardPage'));
const PlayersDashboardPage = lazy(() => import('../../features/dashboard/pages/PlayersDashboardPage'));
const ProblemTrackerDashboardPage = lazy(() => import('../../features/dashboard/pages/ProblemTrackerDashboardPage'));

// Guided-tour sandbox (fake data; in-app tutorial)
const TutorialTablePage = lazy(() => import('../../features/tour/TutorialTablePage'));
const TutorialDeckCheckPage = lazy(() => import('../../features/tour/TutorialDeckCheckPage'));
const TutorialTimerPage = lazy(() => import('../../features/tour/TutorialTimerPage'));
const TutorialJobBoardPage = lazy(() => import('../../features/tour/TutorialJobBoardPage'));
const TutorialDQCasesPage = lazy(() => import('../../features/tour/TutorialDQCasesPage'));
const TutorialPenaltyApprovalPage = lazy(() => import('../../features/tour/TutorialPenaltyApprovalPage'));
const TutorialStaffFeedbackPage = lazy(() => import('../../features/tour/TutorialStaffFeedbackPage'));
const TutorialRoundArchivePage = lazy(() => import('../../features/tour/TutorialRoundArchivePage'));
const TutorialFloorTeamsPage = lazy(() => import('../../features/tour/TutorialFloorTeamsPage'));
const TutorialSeriesPage = lazy(() => import('../../features/tour/TutorialSeriesPage'));
const TutorialCreateTournamentPage = lazy(() => import('../../features/tour/TutorialCreateTournamentPage'));
const TutorialAppealsPage = lazy(() => import('../../features/tour/TutorialAppealsPage'));
const TutorialStaffDashboardPage = lazy(() => import('../../features/tour/TutorialStaffDashboardPage'));
const TutorialTournamentStaffPage = lazy(() => import('../../features/tour/TutorialTournamentStaffPage'));
const TutorialJobApplicationsPage = lazy(() => import('../../features/tour/TutorialJobApplicationsPage'));
const TutorialStoreManagersPage = lazy(() => import('../../features/tour/TutorialStoreManagersPage'));
const TutorialPlayerDashboardPage = lazy(() => import('../../features/tour/TutorialPlayerDashboardPage'));
const TutorialMatchmakingPage = lazy(() => import('../../features/tour/TutorialMatchmakingPage'));
const TutorialTournamentRunnerPage = lazy(() => import('../../features/tour/TutorialTournamentRunnerPage'));

// Job Board
const JobBoardPage = lazy(() => import('../../features/jobs/pages/JobBoardPage'));
const StoreApplicationsPage = lazy(() => import('../../features/store/pages/StoreApplicationsPage'));

// Stream Overlay Manager
const StreamManagerPage = lazy(() => import('../../features/stream/pages/StreamManagerPage'));
const StreamOperatorPage = lazy(() => import('../../features/stream/pages/StreamOperatorPage'));
const StreamCasterPage = lazy(() => import('../../features/stream/pages/StreamCasterPage'));
const StreamSimplePage = lazy(() => import('../../features/stream/pages/StreamSimplePage'));
const StreamEditorPage = lazy(() => import('../../features/stream/pages/StreamEditorPage'));
const StreamOverlayPage = lazy(() => import('../../features/stream/pages/StreamOverlayPage'));

export const staffRoutes: RouteConfig[] = [
  // Post-login picker - shown to authenticated users with both staff
  // and player capability so they can choose which dashboard to enter.
  {
    path: ROUTES.WELCOME,
    element: WelcomePage,
    auth: 'protected',
    roles: [],
  },
  // Landing / Dashboard. Entry-point: a logged-in user with no
  // staff/player picker preference gets bounced to /welcome.
  {
    path: ROUTES.LANDING,
    element: LandingPage,
    auth: 'protected',
    roles: [],
    entryRedirect: true,
  },
  {
    path: ROUTES.FEEDBACK,
    element: FeedbackPage,
    auth: 'protected',
    roles: [],
  },
  // Guided-tour sandboxes — fake practice pages for the tutorial.
  {
    path: ROUTES.TUTORIAL_TABLE,
    element: TutorialTablePage,
    auth: 'protected',
    roles: [],
  },
  {
    path: ROUTES.TUTORIAL_DECKCHECK,
    element: TutorialDeckCheckPage,
    auth: 'protected',
    roles: [],
  },
  { path: ROUTES.TUTORIAL_TIMER, element: TutorialTimerPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_JOBS, element: TutorialJobBoardPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_DQ, element: TutorialDQCasesPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_PENALTY, element: TutorialPenaltyApprovalPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_FEEDBACK, element: TutorialStaffFeedbackPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_ROUNDARCHIVE, element: TutorialRoundArchivePage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_FLOORTEAMS, element: TutorialFloorTeamsPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_SERIES, element: TutorialSeriesPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_CREATE, element: TutorialCreateTournamentPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_APPEALS, element: TutorialAppealsPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_STAFFDASH, element: TutorialStaffDashboardPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_TOURNSTAFF, element: TutorialTournamentStaffPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_JOBAPPS, element: TutorialJobApplicationsPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_STOREMGR, element: TutorialStoreManagersPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_PLAYER, element: TutorialPlayerDashboardPage, auth: 'player', roles: [] },
  { path: ROUTES.TUTORIAL_MATCHMAKING, element: TutorialMatchmakingPage, auth: 'protected', roles: [] },
  { path: ROUTES.TUTORIAL_RUNNER, element: TutorialTournamentRunnerPage, auth: 'protected', roles: [] },

  // Dashboards
  {
    path: ROUTES.TOURNAMENT_DASHBOARD,
    element: TournamentDashboardPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['admin', 'store'],
    allowHeadJudgeTeam: true,
  },
  {
    path: ROUTES.TABLE_MANAGEMENT_DASHBOARD,
    element: TableManagementDashboardPage,
    auth: 'protected',
    roles: [],
    accountTypes: ['admin', 'store', 'staff'],
    allowFloorTeam: true,
  },
  {
    path: ROUTES.DECK_CHECK_DASHBOARD,
    element: DeckCheckDashboardPage,
    auth: 'protected',
    roles: [],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
    allowFloorTeam: true,
  },
  {
    path: ROUTES.PLAYERS_DASHBOARD,
    element: PlayersDashboardPage,
    auth: 'protected',
    roles: [],
    accountTypes: ['admin', 'store', 'staff'],
    allowFloorTeam: true,
  },
  {
    path: ROUTES.PROBLEM_TRACKER_DASHBOARD,
    element: ProblemTrackerDashboardPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
  },

  // Staff Management (store owners can manage staff for their tournaments)
  {
    path: ROUTES.STAFF_MANAGEMENT,
    element: StaffManagementPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STAFF_DASHBOARD,
    element: StaffDashboardPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
    allowFloorTeam: true,
  },
  {
    path: ROUTES.CHAT,
    element: ChatPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
    allowFloorTeam: true,
  },
  {
    path: ROUTES.STAFF_SETTINGS,
    element: StaffSettingsPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STAFF_FLOOR_TEAMS,
    element: FloorTeamsPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
    allowFloorTeam: true,
  },
  {
    path: ROUTES.STAFF_TOURNAMENT_ASSIGNMENTS,
    element: TournamentStaffPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store'],
  },
  {
    // Staff peer-feedback (giving). Open to any approved staff — the page
    // only surfaces series the user actually belongs to.
    path: ROUTES.STAFF_FEEDBACK,
    element: StaffFeedbackPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
    allowFloorTeam: true,
  },

  // User Settings
  {
    path: ROUTES.USER_SETTINGS,
    element: UserSettingsPage,
    auth: 'protected',
    roles: [],
  },

  // Job Board
  {
    path: ROUTES.JOB_BOARD,
    element: JobBoardPage,
    auth: 'protected',
    roles: [],
    accountTypes: ['admin', 'store', 'staff'],
  },
  {
    path: ROUTES.STORE_APPLICATIONS,
    element: StoreApplicationsPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store'],
  },

  // Stream Overlay Manager
  {
    path: ROUTES.STREAM_MANAGER,
    element: StreamManagerPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Stream'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STREAM_OPERATOR,
    element: StreamOperatorPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Stream'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STREAM_CASTER,
    element: StreamCasterPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Stream'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STREAM_SIMPLE,
    element: StreamSimplePage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Stream'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STREAM_EDITOR,
    element: StreamEditorPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Stream'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.STREAM_OVERLAY,
    element: StreamOverlayPage,
    auth: 'open',
    roles: [],
  },
];
